import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Tag, Button, Spinner, Icon } from 'ui';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import {
  StyledMotherDrawer,
  StyledChildList,
  StyledChildRow,
  StyledChildActions,
  StyledMotherDrawerFooter,
} from './style';

interface ChildTransaction {
  id: number;
  invoice_nr: string;
  status: string;
  total_sum: string;
  seller_farm_name?: string;
  order_id: number;
  farm_id: number;
  transaction_type?: string;
}

interface ParentTransaction {
  id: number;
  invoice_nr: string;
  transaction_type?: string;
  total_sum: string;
}

interface MotherTransactionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId: number | null;
  onOpenChildTransaction: (childId: number) => void;
}

const getAggregatedStatus = (children: ChildTransaction[]): string => {
  if (!children.length) return 'new';

  const statuses = children.map((c) => c.status);
  const allSame = statuses.every((s) => s === statuses[0]);

  if (allSame) {
    return statuses[0];
  }

  if (statuses.every((s) => ['canceled', 'cancelled'].includes(s))) {
    return 'canceled';
  }

  if (statuses.some((s) => ['done', 'completed', 'delivered'].includes(s))) {
    if (statuses.every((s) => ['done', 'completed', 'delivered'].includes(s))) {
      return 'done';
    }
    return 'partially_done';
  }

  if (statuses.some((s) => s === 'sent')) {
    if (statuses.every((s) => s === 'sent')) {
      return 'sent';
    }
    return 'partially_sent';
  }

  if (statuses.some((s) => s === 'partially_sent')) {
    return 'partially_sent';
  }

  if (statuses.some((s) => s === 'pending_send')) {
    return 'pending_send';
  }

  if (statuses.some((s) => s === 'confirmed')) {
    if (statuses.every((s) => s === 'confirmed')) {
      return 'confirmed';
    }
    return 'partially_confirmed';
  }

  return 'new';
};

const MotherTransactionDrawer = ({
  isOpen,
  onClose,
  transactionId,
  onOpenChildTransaction,
}: MotherTransactionDrawerProps) => {
  const intl = useIntl();
  const [parentTransaction, setParentTransaction] =
    useState<ParentTransaction | null>(null);
  const [children, setChildren] = useState<ChildTransaction[]>([]);

  const { appendData: fetchParent, isLoading: isLoadingParent } =
    useQueryApiClient({
      request: {
        url: 'api/v2/transactions/:transactionId',
        disableOnMount: true,
      },
      onSuccess: (response: ParentTransaction) => {
        setParentTransaction(response);
      },
    });

  const { appendData: fetchChildren, isLoading: isLoadingChildren } =
    useQueryApiClient({
      request: {
        url: 'api/v2/transactions/:transactionId/children',
        disableOnMount: true,
      },
      onSuccess: (response: ChildTransaction[]) => {
        setChildren(response);
      },
    });

  useEffect(() => {
    if (isOpen && transactionId) {
      fetchParent({}, { transactionId });
      fetchChildren({}, { transactionId });
    }
  }, [isOpen, transactionId]);

  useEffect(() => {
    if (!isOpen) {
      setParentTransaction(null);
      setChildren([]);
    }
  }, [isOpen]);

  const handleOpenChild = (childId: number) => {
    onClose();
    onOpenChildTransaction(childId);
  };

  const aggregatedStatus = getAggregatedStatus(children);
  const isLoading = isLoadingParent || isLoadingChildren;

  const transactionTypeLabel = intl.formatMessage({
    id: 'transactions.order_transaction',
  });

  const renderTitle = () => (
    <div className="custom-header">
      <div className="header-top">
        <span className="transaction-type">{transactionTypeLabel}</span>
        <Tag className="status-tag">
          {intl.formatMessage({ id: `enum.${aggregatedStatus.toUpperCase()}` })}
        </Tag>
      </div>
      <div className="transaction-nr">
        {intl.formatMessage({ id: 'transactions.order_nr' })}:{' '}
        {parentTransaction?.invoice_nr}
      </div>
    </div>
  );

  return (
    <StyledMotherDrawer
      open={isOpen}
      onClose={onClose}
      title={renderTitle()}
      width={923}
      destroyOnClose
      zIndex={1200}
      closeIcon={<Icon name={'global-close-icon'} />}
      getContainer={document.getElementById('app-root')!}
      footer={
        <StyledMotherDrawerFooter>
          <Button
            label={intl.formatMessage({ id: 'general.close' })}
            onClick={onClose}
          />
        </StyledMotherDrawerFooter>
      }
    >
      <Spinner spinning={isLoading}>
        <StyledChildList>
          {children.length === 0 ? (
            <div className="no-children">
              {intl.formatMessage({ id: 'transactions.no_child_transactions' })}
            </div>
          ) : (
            children.map((child) => (
              <StyledChildRow key={child.id}>
                <Tag className={`child-status-tag status-${child.status}`}>
                  {intl.formatMessage({
                    id: `enum.${child.status.toUpperCase()}`,
                  })}
                </Tag>
                <span className="child-invoice">
                  {intl.formatMessage({ id: 'transactions.order_nr' })}:{' '}
                  {child.invoice_nr}
                </span>
                <span className="child-farm">
                  {child.seller_farm_name || '-'}
                </span>
                <span className="child-amount">
                  {Number(child.total_sum || 0).toFixed(2)} EUR
                </span>
                <StyledChildActions>
                  <Button
                    label={intl.formatMessage({ id: 'transactions.open' })}
                    onClick={() => handleOpenChild(child.id)}
                  />
                </StyledChildActions>
              </StyledChildRow>
            ))
          )}
        </StyledChildList>
      </Spinner>
    </StyledMotherDrawer>
  );
};

export default MotherTransactionDrawer;
